import React from 'react';
import { Box, Typography } from '@mui/material';
import { PlayArrow, Movie, Tv } from '@mui/icons-material';

const HypeCircleLogo = ({ 
  size = 'md', 
  variant = 'default',
  theme = 'auto',
  animated = true,
  className = '',
  sx = {}
}) => {
  const sizeClasses = {
    sm: { width: 32, height: 32 },
    md: { width: 48, height: 48 },
    lg: { width: 64, height: 64 },
    xl: { width: 96, height: 96 }
  };

  const textSizes = {
    sm: 'h6',
    md: 'h5',
    lg: 'h4',
    xl: 'h3'
  };

  const getThemeColors = (muiTheme) => {
    const isDark = muiTheme?.palette?.mode === 'dark';
    
    if (theme === 'light' || variant === 'light') {
      return {
        primary: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 50%, #1565c0 100%)',
        accent: '#1976d2',
        border: 'rgba(25, 118, 210, 0.3)',
        glow: 'rgba(25, 118, 210, 0.2)',
        playButton: '#1976d2',
        dots: '#42a5f5'
      };
    } else if (theme === 'dark' || variant === 'dark') {
      return {
        primary: 'linear-gradient(135deg, #ffc107 0%, #ffeb3b 50%, #ff8f00 100%)',
        accent: '#ffc107',
        border: 'rgba(255, 193, 7, 0.3)',
        glow: 'rgba(255, 193, 7, 0.2)',
        playButton: '#ffc107',
        dots: '#ffeb3b'
      };
    } else {
      return {
        primary: isDark 
          ? 'linear-gradient(135deg, #ffc107 0%, #ffeb3b 50%, #ff8f00 100%)'
          : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 50%, #1565c0 100%)',
        accent: isDark ? '#ffc107' : '#1976d2',
        border: isDark ? 'rgba(255, 193, 7, 0.3)' : 'rgba(25, 118, 210, 0.3)',
        glow: isDark ? 'rgba(255, 193, 7, 0.2)' : 'rgba(25, 118, 210, 0.2)',
        playButton: isDark ? '#ffc107' : '#1976d2',
        dots: isDark ? '#ffeb3b' : '#42a5f5'
      };
    }
  };

  if (variant === 'icon') {
    return (
      <Box
        className={className}
        sx={{
          position: 'relative',
          ...sizeClasses[size],
          ...sx
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            backdropFilter: 'blur(20px)',
            background: (theme) => {
              const colors = getThemeColors(theme);
              return `${colors.primary}20`;
            },
            border: (theme) => `1px solid ${getThemeColors(theme).border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              width: size === 'sm' ? 16 : size === 'md' ? 24 : size === 'lg' ? 32 : 40,
              height: size === 'sm' ? 16 : size === 'md' ? 24 : size === 'lg' ? 32 : 40,
              borderRadius: '50%',
              background: (theme) => getThemeColors(theme).primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 3
            }}
          >
            <PlayArrow 
              sx={{ 
                color: 'white', 
                fontSize: size === 'sm' ? 12 : size === 'md' ? 16 : size === 'lg' ? 20 : 24
              }} 
            />
          </Box>
        </Box>
      </Box>
    );
  }

  if (variant === 'text') {
    return (
      <Box className={className} sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
        <Typography 
          variant={textSizes[size]} 
          sx={{
            fontWeight: 'bold',
            background: (theme) => getThemeColors(theme).primary,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textFillColor: 'transparent'
          }}
        >
          HypeCircle
        </Typography>
      </Box>
    );
  }

  // Default variant
  return (
    <Box className={className} sx={{ display: 'flex', alignItems: 'center', gap: 2, ...sx }}>
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            ...sizeClasses[size],
            borderRadius: '50%',
            backdropFilter: 'blur(20px)',
            background: (theme) => {
              const isDark = theme.palette.mode === 'dark';
              return isDark 
                ? 'linear-gradient(135deg, rgba(255,193,7,0.15) 0%, rgba(255,235,59,0.1) 50%, rgba(255,143,0,0.15) 100%)'
                : 'linear-gradient(135deg, rgba(25,118,210,0.15) 0%, rgba(66,165,245,0.1) 50%, rgba(21,101,192,0.15) 100%)';
            },
            border: (theme) => {
              const isDark = theme.palette.mode === 'dark';
              return isDark 
                ? '1px solid rgba(255,193,7,0.3)'
                : '1px solid rgba(25,118,210,0.3)';
            },
            boxShadow: (theme) => {
              const isDark = theme.palette.mode === 'dark';
              return isDark
                ? '0 8px 32px rgba(255,193,7,0.2), inset 0 1px 0 rgba(255,255,255,0.1)'
                : '0 8px 32px rgba(25,118,210,0.2), inset 0 1px 0 rgba(255,255,255,0.2)';
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '50%',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
              borderRadius: '50% 50% 0 0'
            }
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Animated dotted ring */}
            <Box
              sx={{
                position: 'absolute',
                inset: '6px',
                borderRadius: '50%',
                border: (theme) => `2px dashed ${getThemeColors(theme).border}`,
                animation: animated ? 'spin 8s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            />
            <Box
              sx={{
                width: size === 'sm' ? 18 : size === 'md' ? 24 : size === 'lg' ? 32 : 40,
                height: size === 'sm' ? 18 : size === 'md' ? 24 : size === 'lg' ? 32 : 40,
                borderRadius: '50%',
                backdropFilter: 'blur(10px)',
                background: (theme) => {
                  const isDark = theme.palette.mode === 'dark';
                  return isDark 
                    ? 'linear-gradient(135deg, rgba(255,193,7,0.8) 0%, rgba(255,235,59,0.6) 50%, rgba(255,143,0,0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(25,118,210,0.8) 0%, rgba(66,165,245,0.6) 50%, rgba(21,101,192,0.8) 100%)';
                },
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 10
              }}
            >
              <Box
                sx={{
                  width: 0,
                  height: 0,
                  borderLeft: `${size === 'sm' ? 7 : size === 'md' ? 9 : size === 'lg' ? 11 : 14}px solid rgba(255,255,255,0.95)`,
                  borderTop: `${size === 'sm' ? 5 : size === 'md' ? 7 : size === 'lg' ? 9 : 11}px solid transparent`,
                  borderBottom: `${size === 'sm' ? 5 : size === 'md' ? 7 : size === 'lg' ? 9 : 11}px solid transparent`,
                  ml: '2px',
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))'
                }}
              />
            </Box>
            
            {/* Glass orbs */}
            {[
              { top: '10px', left: '10px', delay: '0s' },
              { top: '10px', right: '10px', delay: '0.5s' },
              { bottom: '10px', left: '14px', delay: '1s' },
              { bottom: '10px', right: '14px', delay: '1.5s' }
            ].map((dot, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  width: size === 'sm' ? 6 : size === 'md' ? 8 : 10,
                  height: size === 'sm' ? 6 : size === 'md' ? 8 : 10,
                  borderRadius: '50%',
                  backdropFilter: 'blur(4px)',
                  background: (theme) => {
                    const isDark = theme.palette.mode === 'dark';
                    return isDark 
                      ? 'linear-gradient(135deg, rgba(255,235,59,0.6) 0%, rgba(255,193,7,0.4) 100%)'
                      : 'linear-gradient(135deg, rgba(66,165,245,0.6) 0%, rgba(25,118,210,0.4) 100%)';
                  },
                  border: '1px solid rgba(255,255,255,0.2)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                  animation: animated ? `float 3s ease-in-out infinite ${dot.delay}` : 'none',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0px) scale(1)' },
                    '50%': { transform: 'translateY(-3px) scale(1.1)' }
                  },
                  ...dot
                }}
              />
            ))}
            
            {/* Entertainment icons */}
            <Movie 
              sx={{ 
                position: 'absolute', 
                top: '4px', 
                left: '4px',
                fontSize: size === 'sm' ? 10 : size === 'md' ? 12 : 14,
                color: (theme) => getThemeColors(theme).playButton,
                animation: animated ? 'pulse 3s infinite' : 'none',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.5 }
                }
              }} 
            />
            <Tv 
              sx={{ 
                position: 'absolute', 
                top: '4px', 
                right: '4px',
                fontSize: size === 'sm' ? 10 : size === 'md' ? 12 : 14,
                color: (theme) => getThemeColors(theme).playButton,
                animation: animated ? 'pulse 3s infinite 0.8s' : 'none'
              }} 
            />
          </Box>
        </Box>
        
        {/* Premium glow effect */}
        <Box
          sx={{
            position: 'absolute',
            inset: '-4px',
            borderRadius: '50%',
            background: (theme) => {
              const isDark = theme.palette.mode === 'dark';
              return isDark
                ? 'radial-gradient(circle, rgba(255,193,7,0.15) 0%, rgba(255,235,59,0.1) 40%, transparent 70%)'
                : 'radial-gradient(circle, rgba(25,118,210,0.15) 0%, rgba(66,165,245,0.1) 40%, transparent 70%)';
            },
            animation: animated ? 'breathe 3s ease-in-out infinite' : 'none',
            '@keyframes breathe': {
              '0%, 100%': { opacity: 0.6, transform: 'scale(1)' },
              '50%': { opacity: 1, transform: 'scale(1.05)' }
            },
            filter: 'blur(8px)'
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography 
          variant={textSizes[size]} 
          sx={{
            fontWeight: 'bold',
            background: (theme) => getThemeColors(theme).primary,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textFillColor: 'transparent',
            lineHeight: 1
          }}
        >
          HypeCircle
        </Typography>
        <Typography 
          variant="caption" 
          sx={{
            color: 'text.secondary',
            fontSize: size === 'sm' ? '0.6rem' : size === 'md' ? '0.7rem' : '0.8rem',
            letterSpacing: '0.5px',
            mt: 0.5
          }}
        >
          Movies & TV
        </Typography>
      </Box>
    </Box>
  );
};

export default HypeCircleLogo;